import { Link } from 'react-router-dom';
import { Instagram, Phone, Mail, MapPin, ChevronDown } from 'lucide-react';
import { useCategories } from '@/hooks/use-products';
import { useState, type ReactNode } from 'react';

function FooterSection({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border md:border-0 last:border-b-0">
      {/* Mobile: collapsible header */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="md:hidden flex w-full items-center justify-between py-2.5 text-left"
        aria-expanded={open}
      >
        <span className="font-heading text-base font-semibold">{title}</span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {/* Desktop: always-visible header */}
      <h4 className="hidden md:block font-heading font-semibold mb-2 lg:mb-4 text-sm lg:text-base">{title}</h4>
      <div
        className={`overflow-hidden transition-[max-height] duration-300 md:max-h-none md:overflow-visible ${
          open ? 'max-h-[600px] pb-3' : 'max-h-0 md:pb-0'
        }`}
      >
        {children}
      </div>
    </div>
  );
}

export default function Footer() {
  const { data: categories = [] } = useCategories();
  const categoryOrder = ['damen', 'herren', 'personalisierte-blaetter', 'geschenkideen', 'dekoration'];
  const parentCategories = categories
    .filter((c) => !c.parent_id)
    .sort((a, b) => {
      const ai = categoryOrder.indexOf(a.slug);
      const bi = categoryOrder.indexOf(b.slug);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

  return (
    <footer className="border-t border-border bg-card mt-12 md:mt-14 lg:mt-20">
      <div className="container py-7 md:py-8 lg:py-14">
        {/* Brand block – always visible */}
        <div className="mb-4 md:mb-0 md:grid md:grid-cols-4 md:gap-5 lg:gap-10">
          <div className="md:col-span-1 mb-4 md:mb-0">
            <Link to="/" className="flex items-center gap-2 mb-2 md:mb-3">
              <span className="font-heading text-xl md:text-lg lg:text-2xl font-bold text-primary">cycy</span>
              <span className="text-xs font-body text-muted-foreground tracking-widest uppercase">art</span>
            </Link>
            <p className="text-sm md:text-xs lg:text-sm text-muted-foreground leading-relaxed max-w-sm">
              Handgefertigte Kunstwerke, individueller Modeschmuck und personalisierte Gravuren – mit Liebe gefertigt.
            </p>
          </div>

          <FooterSection title="Shop">
            <div className="space-y-2">
              {parentCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/shop?category=${cat.slug}`}
                  className="block text-sm text-muted-foreground hover:text-primary transition-colors py-2 md:py-1"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </FooterSection>

          <FooterSection title="Links">
            <div className="space-y-2">
              <Link to="/ueber-uns" className="block text-sm text-muted-foreground hover:text-primary transition-colors py-2 md:py-1">Über uns</Link>
              <Link to="/galerie" className="block text-sm text-muted-foreground hover:text-primary transition-colors py-2 md:py-1">Galerie</Link>
              <Link to="/#kontakt" className="block text-sm text-muted-foreground hover:text-primary transition-colors py-2 md:py-1">Kontakt</Link>
            </div>
          </FooterSection>

          <FooterSection title="Kontakt" defaultOpen>
            <div className="space-y-3 md:space-y-1.5 text-sm md:text-xs lg:text-sm text-muted-foreground">
              <a href="tel:+4917657861376" className="flex items-center gap-2 hover:text-primary transition-colors">
                <Phone className="h-4 w-4 shrink-0" /> +49 176 578 613 76
              </a>
              <a href="mailto:cycyart1314@gmail.com" className="flex items-center gap-2 hover:text-primary transition-colors break-all">
                <Mail className="h-4 w-4 shrink-0" /> cycyart1314@gmail.com
              </a>
              <a href="https://www.google.com/maps/search/?api=1&query=Westfalenstraße+55,+40472+Düsseldorf" target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 hover:text-primary transition-colors">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0" /> Westfalenstraße 55, 40472 Düsseldorf
              </a>
              <div className="flex items-center gap-3 pt-1">
                <a href="https://www.instagram.com/cycyart_geschenke" target="_blank" rel="noopener noreferrer" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary hover:bg-primary/10 hover:text-primary transition-colors" aria-label="Instagram">
                  <Instagram className="h-4 w-4" />
                </a>
                <a href="https://www.tiktok.com/@cycyart_geschenke" target="_blank" rel="noopener noreferrer" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary hover:bg-primary/10 hover:text-primary transition-colors" aria-label="TikTok">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.75a8.18 8.18 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.18z"/></svg>
                </a>
              </div>
            </div>
          </FooterSection>
        </div>

        <div className="border-t border-border mt-4 md:mt-5 lg:mt-8 pt-4 md:pt-5 lg:pt-8">
          <nav aria-label="Rechtliches" className="flex flex-wrap justify-center gap-x-3 gap-y-0.5 sm:gap-x-4 text-xs text-muted-foreground mb-2 md:mb-4">
            <Link to="/impressum" className="inline-flex items-center min-h-8 md:min-h-10 px-1 hover:text-primary transition-colors">Impressum</Link>
            <Link to="/datenschutz" className="inline-flex items-center min-h-8 md:min-h-10 px-1 hover:text-primary transition-colors">Datenschutz</Link>
            <Link to="/agb" className="inline-flex items-center min-h-8 md:min-h-10 px-1 hover:text-primary transition-colors">AGB</Link>
            <Link to="/widerrufsbelehrung" className="inline-flex items-center min-h-8 md:min-h-10 px-1 hover:text-primary transition-colors">Widerruf</Link>
            <Link to="/zahlung-versand" className="inline-flex items-center min-h-8 md:min-h-10 px-1 hover:text-primary transition-colors whitespace-nowrap">Zahlung&nbsp;&amp;&nbsp;Versand</Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("cycy:open-cookie-settings"))}
              className="inline-flex items-center min-h-8 md:min-h-10 px-1 hover:text-primary transition-colors"
            >
              Cookies
            </button>
          </nav>
          <p className="text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} cycy art. Alle Rechte vorbehalten.
          </p>
        </div>
      </div>
    </footer>
  );
}
